/**
 * ScoreGradeComponent
 * Displays AI grading results for essay content: overall score, criteria breakdown,
 * detailed feedback (strengths, weaknesses, corrections), and suggested revision.
 */

import * as React from 'react';
import { CheckCircle2, AlertCircle, Lightbulb, HelpCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { kitT, type KitTranslateFn } from '../../lib/kitTranslate';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui';

const RING_RADIUS = 15.5;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export interface ScoreGradeCriteria {
    score: number;
    max: number;
    description?: string;
}

export interface ScoreGradeDetailedFeedback {
    strengths?: string[];
    weaknesses?: string[];
    corrections?: string[];
}

export interface ScoreGradeResult {
    overall_score_percentage: number;
    word_count?: number;
    criteria_scores?: Record<string, ScoreGradeCriteria>;
    detailed_feedback?: ScoreGradeDetailedFeedback;
    suggested_revision?: string;
}

export interface ScoreGradeComponentProps {
    result: ScoreGradeResult;
    className?: string;
    translate?: KitTranslateFn;
}

const CRITERIA_I18N_KEYS: Record<string, string> = {
    content: 'kSgcCritContent',
    form: 'kSgcCritForm',
    grammar: 'kSgcCritGrammar',
    vocabulary: 'kSgcCritVocabulary',
    spelling: 'kSgcCritSpelling',
    // ELMS-2280: essay rubric (QT_WRITE_ESSAY) returns two more criteria than the
    // 5-criteria summary rubrics — without labels they rendered as raw keys.
    dsc: 'kSgcCritDsc',
    glr: 'kSgcCritGlr',
};

const CRITERIA_FALLBACKS: Record<string, string> = {
    content: 'Content',
    form: 'Form',
    grammar: 'Grammar',
    vocabulary: 'Vocabulary',
    spelling: 'Spelling',
    dsc: 'Development & Coherence',
    glr: 'Language Range',
};

function getCriteriaLabel(key: string, translate?: KitTranslateFn): string {
    const i18nKey = CRITERIA_I18N_KEYS[key];
    if (i18nKey) {
        return kitT(translate, i18nKey, CRITERIA_FALLBACKS[key] ?? key);
    }
    return key;
}

function getScoreLabel(percentage: number, translate?: KitTranslateFn): string {
    if (percentage >= 100) return kitT(translate, 'kSgcScorePerfect', 'Perfect!');
    if (percentage >= 80) return kitT(translate, 'kSgcScoreGreat', 'Great job!');
    if (percentage >= 60) return kitT(translate, 'kSgcScoreGood', 'Good effort!');
    if (percentage >= 40) return kitT(translate, 'kSgcScorePractice', 'Keep practicing!');
    return kitT(translate, 'kSgcScoreRetry', 'Try again!');
}

function CircularProgressRing({
    percentage,
    isPassing,
}: {
    percentage: number;
    isPassing: boolean;
}) {
    const offset = RING_CIRCUMFERENCE * (1 - Math.min(percentage, 100) / 100);
    const activeColor = isPassing
        ? 'hsl(var(--success))'
        : 'hsl(var(--destructive))';

    return (
        <div
            className="score-grade-ring relative shrink-0"
            style={{ width: 80, height: 80 }}
        >
            <svg
                viewBox="0 0 36 36"
                className="w-full h-full"
                style={{ transform: 'rotate(-90deg)' }}
            >
                <circle
                    cx="18"
                    cy="18"
                    r={RING_RADIUS}
                    fill="none"
                    style={{ stroke: 'hsl(var(--muted))' }}
                    strokeWidth="3.5"
                />
                <circle
                    cx="18"
                    cy="18"
                    r={RING_RADIUS}
                    fill="none"
                    style={{
                        stroke: activeColor,
                        strokeDasharray: RING_CIRCUMFERENCE,
                        strokeDashoffset: offset,
                        transition: 'stroke-dashoffset 0.8s ease-out',
                    }}
                    strokeWidth="3.5"
                    strokeLinecap="round"
                />
            </svg>
            <span
                className={cn(
                    'absolute inset-0 flex items-center justify-center text-lg font-bold',
                    isPassing ? 'text-success' : 'text-destructive'
                )}
            >
                {percentage.toFixed(0)}%
            </span>
        </div>
    );
}

export const ScoreGradeComponent: React.FC<ScoreGradeComponentProps> = ({
    result,
    className,
    translate,
}) => {
    const percentage = result.overall_score_percentage ?? 0;
    const isPassing = percentage >= 60;
    const criteriaScores = result.criteria_scores ?? {};
    const feedback = result.detailed_feedback ?? {};
    const strengths = feedback.strengths ?? [];
    const weaknesses = feedback.weaknesses ?? [];
    const corrections = feedback.corrections ?? [];

    return (
        <div
            role="region"
            aria-live="polite"
            aria-label={kitT(translate, 'kSgcRegionLabel', 'Essay grading results')}
            className={cn(
                'score-grade-component min-w-0 rounded-xl border border-border shadow-sm p-4 md:p-6 space-y-6 break-words',
                className
            )}
        >
            {/* Hero: circular progress + label + word count */}
            <div className="score-grade-hero flex items-center gap-4">
                <CircularProgressRing percentage={percentage} isPassing={isPassing} />
                <div className="flex-1 min-w-0">
                    <h4
                        className={cn(
                            'text-lg font-semibold',
                            isPassing ? 'text-success' : 'text-destructive'
                        )}
                    >
                        {getScoreLabel(percentage, translate)}
                    </h4>
                    {result.word_count != null && (
                        <span
                            className="inline-flex mt-1 px-3 py-1 rounded-full text-sm font-medium bg-muted text-muted-foreground"
                        >
                            {kitT(translate, 'kSgcWordCount', '{n} words').replace(
                                '{n}',
                                String(result.word_count)
                            )}
                        </span>
                    )}
                </div>
            </div>

            {/* Criteria grid */}
            {Object.keys(criteriaScores).length > 0 && (
                <TooltipProvider delayDuration={0}>
                    <div className="score-grade-criteria grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.entries(criteriaScores).map(([key, crit]) => {
                            const label = getCriteriaLabel(key, translate);
                            const pct = crit.max > 0 ? (crit.score / crit.max) * 100 : 0;
                            return (
                                <div
                                    key={key}
                                    className="score-grade-criteria-card min-w-0 rounded-lg border border-border bg-muted/30 p-3 break-words"
                                >
                                    <div className="flex items-center justify-between text-sm font-medium gap-2 min-w-0">
                                        <span className="flex items-center gap-1.5 min-w-0">
                                            {label}
                                            {crit.description && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            aria-label={kitT(
                                                                translate,
                                                                'kSgcAriaCriteriaDesc',
                                                                'Show criteria description'
                                                            )}
                                                            className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                                                        >
                                                            <HelpCircle className="h-4 w-4" />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent
                                                        side="top"
                                                        className="max-w-64"
                                                    >
                                                        {crit.description}
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                        </span>
                                        <span>
                                            {crit.score} / {crit.max}
                                        </span>
                                    </div>
                                    <div
                                        className="mt-2 h-1.5 rounded-full overflow-hidden"
                                        style={{ backgroundColor: 'hsl(var(--muted))' }}
                                    >
                                        <div
                                            className="h-full rounded-full transition-[width] duration-500 ease-out"
                                            style={{
                                                width: `${Math.min(pct, 100)}%`,
                                                backgroundColor: pct >= 60 ? 'hsl(var(--success))' : 'hsl(var(--destructive))',
                                            }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </TooltipProvider>
            )}

            {/* Strengths */}
            {/* {strengths.length > 0 && (
                <div
                    className="score-grade-feedback rounded-xl p-4 bg-success/10 border border-success/30"
                >
                    <h5 className="text-lg font-semibold text-success flex items-center gap-2 mb-2">
                        <CheckCircle2 className="h-5 w-5" />
                        Strengths
                    </h5>
                    <ul className="space-y-2 text-base text-foreground">
                        {strengths.map((item, i) => (
                            <li key={i}>{item}</li>
                        ))}
                    </ul>
                </div>
            )} */}

            {/* Weaknesses */}
            {/* {weaknesses.length > 0 && (
                <div
                    className="score-grade-feedback rounded-xl p-4 bg-warning/10 border border-warning/30"
                >
                    <h5 className="text-lg font-semibold text-warning flex items-center gap-2 mb-2">
                        <AlertCircle className="h-5 w-5" />
                        Areas to improve
                    </h5>
                    <ul className="space-y-2 text-base text-foreground">
                        {weaknesses.map((item, i) => (
                            <li key={i}>{item}</li>
                        ))}
                    </ul>
                </div>
            )} */}

            {/* Corrections */}
            {/* {corrections.length > 0 && (
                <div
                    className="score-grade-feedback rounded-xl p-4 bg-info/10 border border-info/30"
                >
                    <h5 className="text-lg font-semibold text-info flex items-center gap-2 mb-2">
                        <Lightbulb className="h-5 w-5" />
                        Corrections
                    </h5>
                    <ul className="space-y-2 text-base text-foreground">
                        {corrections.map((item, i) => (
                            <li key={i}>{item}</li>
                        ))}
                    </ul>
                </div>
            )} */}

            {/* Suggested revision */}
            {/* {result.suggested_revision && (
                <div
                    className="score-grade-suggested-revision rounded-xl p-4 bg-primary/5 border border-primary/20"
                >
                    <h5 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-2">
                        <Lightbulb className="h-5 w-5 text-primary" />
                        Suggested revision
                    </h5>
                    <p className="text-base text-foreground">
                        {result.suggested_revision}
                    </p>
                </div>
            )} */}
        </div>
    );
};

ScoreGradeComponent.displayName = 'ScoreGradeComponent';
